import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import { motion, easeInOut } from 'framer-motion';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SecurityIcon from '@mui/icons-material/Security';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import InventoryIcon from '@mui/icons-material/Inventory';
import EvStationIcon from '@mui/icons-material/EvStation';
import Banner1 from '../../../assets/banner1-cropped.jpg';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useTheme as useAppTheme } from '../../../contexts/ThemeContext';

const features = [
    {
        icon: <LocalShippingIcon sx={{ fontSize: 56 }} />,
        title: 'Timely Deliveries',
        description: 'Up to 3 delivery attempts within 72 hours for every shipment.'
    },
    {
        icon: <SecurityIcon sx={{ fontSize: 56 }} />,
        title: '24–48 Hrs Cash Back',
        description: 'Get your cash back within 24 to 48 hours after delivery.'
    },
    {
        icon: <TrackChangesIcon sx={{ fontSize: 56 }} />,
        title: 'Real-time Finance Updates',
        description: 'Monitor your finances with instant, real-time updates.'
    },
    {
        icon: <AnalyticsIcon sx={{ fontSize: 56 }} />,
        title: 'Advanced Customer Portal',
        description: 'Interact and manage shipments with our advanced online portal.'
    },
    {
        icon: <InventoryIcon sx={{ fontSize: 56 }} />,
        title: 'Online Account Request',
        description: 'Easily request and manage your account online.'
    },
    {
        icon: <EvStationIcon sx={{ fontSize: 56 }} />,
        title: 'No Fuel/Return Charges',
        description: 'No fuel surcharge and no return charges for your shipments.'
    },
];

const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({
        opacity: 1,
        y: 0,
        transition: { delay: 0.3 + i * 0.15, duration: 0.6, ease: easeInOut },
    }),
};

const ARROW_WIDTH = 80; // px

const TopFeaturesSection = () => {
    const [hovered, setHovered] = useState<number | null>(null);
    const { mode } = useAppTheme();

    // Dynamic background color based on theme
    const getBackgroundColor = () => {
        return mode === 'dark' ? '#18191a' : '#f8f9fa';
    };

    return (
        <Box
            sx={{
                minHeight: '85vh',
                height: { xs: 'auto', md: '85vh' },
                width: '100%',
                position: 'relative',
                display: { xs: 'block', md: 'flex' },
                alignItems: 'stretch',
                bgcolor: getBackgroundColor(),
                overflow: { xs: 'visible', md: 'hidden' },
            }}
        >
            {/* Left: Banner Image, with right-edge arrow cut */}
            <Box
                sx={{
                    display: { xs: 'none', md: 'block' },
                    width: `calc(40% + ${ARROW_WIDTH / 2}px)`,
                    minWidth: `calc(40% + ${ARROW_WIDTH / 2}px)`,
                    height: '100%',
                    minHeight: '85vh',
                    position: 'relative',
                    zIndex: 1,
                    backgroundImage: `url(${Banner1})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: mode === 'dark' ? 'brightness(0.85)' : 'brightness(0.95)',
                    clipPath: 'polygon(0 0, 90% 0, 100% 50%, 90% 100%, 0 100%)',
                }}
            />

            {/* Right: Features, responsive for mobile */}
            <Box
                sx={{
                    display: { xs: 'block', md: 'flex' },
                    flex: 1,
                    position: 'relative',
                    zIndex: 2,
                    bgcolor: 'transparent',
                    px: { xs: 2, md: `${ARROW_WIDTH}px` },
                    py: { xs: 6, md: 0 },
                    clipPath: { xs: 'none', md: `polygon(${ARROW_WIDTH}px 0, 100% 0, 100% 100%, ${ARROW_WIDTH}px 100%, 0 50%)` },
                    minHeight: { xs: 'auto', md: '85vh' },
                    height: { xs: 'auto', md: '100%' },
                    alignItems: 'center',
                    overflow: { xs: 'visible', md: 'hidden' },
                }}
            >
                <Box sx={{ width: '100%' }}>
                    <motion.div
                        initial={{ opacity: 0, y: -40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.6 }}
                        transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
                    >
                        <Typography variant="h3" sx={{ fontWeight: 900, color: 'secondary.main', textAlign: 'center', mb: 5, letterSpacing: 2, fontSize: { xs: 24, sm: 32, lg: 48 } }}>
                            Why Choose Us?
                        </Typography>
                    </motion.div>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, width: '100%' }}>
                        {features.map((feature, idx) => (
                            <Box 
                                sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 16px)' } }}
                                key={feature.title}
                            >
                                <motion.div
                                    custom={idx}
                                    variants={cardVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    style={{ height: '100%' }}
                                >
                                    <Box 
                                        sx={{ 
                                            display: 'flex', 
                                            alignItems: 'flex-start', 
                                            gap: 3, 
                                            minHeight: 120,
                                            bgcolor: { xs: 'background.paper', md: 'transparent' },
                                            borderRadius: { xs: 3, md: 0 },
                                            p: { xs: 2.5, md: 0 },
                                            boxShadow: { xs: 1, md: 'none' },
                                        }}
                                    >
                                        <motion.div
                                            whileHover={{ scale: 1.12 }}
                                            onHoverStart={() => setHovered(idx)}
                                            onHoverEnd={() => setHovered(null)}
                                            style={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            <Box
                                                sx={{
                                                    width: { xs: 56, md: 64, lg: 80 },
                                                    height: { xs: 56, md: 64, lg: 80 },
                                                    borderRadius: { xs: 4 },
                                                    bgcolor: hovered === idx ? 'secondary.main' : 'rgba(0,0,0,0.32)',
                                                    color: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: hovered === idx ? '0 8px 32px 0 rgba(188,34,34,0.25)' : 2,
                                                    transition: 'all 0.25s',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {feature.icon}
                                            </Box>
                                        </motion.div>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1, fontSize: { xs: 16, md: 16, lg: 18 } }}>{feature.title}</Typography>
                                            <Typography variant="body1" sx={{ color: 'text.secondary', fontSize: { xs: 14, md: 14, lg: 16 } }}>{feature.description}</Typography>
                                        </Box>
                                    </Box>
                                </motion.div>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            {/* Mobile: fallback to stacked layout with no arrow */}
            <Box
                sx={{
                    display: { xs: 'block', md: 'none' },
                    position: 'absolute',
                    inset: 0,
                    backgroundImage: `url(${Banner1})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.18,
                    zIndex: 0,
                }}
            />
        </Box>
    );
};

export default TopFeaturesSection;